import { template } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { service } from '@ember/service';
import type DocsService from '../services/kolay/docs.ts';
import type RouterService from '@ember/routing/router-service';
export class GroupNav extends Component<{
    /**
   * The `<nav>` element. It has a default `aria-label` of "Groups".
   * Normally an `aria-label` is not required,
   * but when there are multiple `<nav>` elements on a screen, it is required.
   */ Element: HTMLElement;
    Args: {
        /**
     * The class to apply to the `<a>` element when its link is active.
     *
     * Defaults to "active"
     */ activeClass?: string;
        /**
     * The text to use for the "/" link.
     *
     * Defaults to "Home".
     * This type of link is commonly set to the library name.
     */ homeName?: string;
    };
    Blocks: {
        /**
     * If you'd like to customize how the links are formatted,
     * you may pass a block which which will have the name of each link
     * yielded to it.
     *
     * Example:
     * ```gjs
     * import { GroupNav } from 'kolay/components';
     *
     * const format = text => text.toUpperCase();
     *
     * <template>
     *   <GroupNav as |name|>
     *     {{format name}}
     *   </GroupNav>
     * </template>
     * ```
     */ default: [name: string];
    };
}> {
    @service('kolay/docs')
    docs: DocsService;
    @service
    router: RouterService;
    get homeName() {
        return this.args.homeName ?? 'Home';
    }
    get groups() {
        return this.docs.availableGroups.map((groupName1)=>{
            if (groupName1 === 'root') return {
                text: this.homeName,
                value: '/'
            };
            return {
                text: groupName1,
                value: `/${groupName1}`
            };
        });
    }
    isActive = (subPath1: string)=>{
        if (subPath1 === '/') return false;
        return this.router.currentURL?.startsWith(subPath1);
    };
    get activeClass() {
        return this.args.activeClass ?? 'active';
    }
    static{
        template(`
    <nav aria-label='Groups' ...attributes>
      <ul>
        {{#each this.groups as |group|}}
          <li>
            <a
              href={{group.value}}
              class={{if (this.isActive group.value) this.activeClass}}
            >

              {{#if (has-block)}}
                {{yield group.text}}
              {{else}}
                {{group.text}}
              {{/if}}

            </a>
          </li>
        {{/each}}
      </ul>
    </nav>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbIi9ob21lL252cC9EZXZlbG9wbWVudC9OdWxsVm94UG9wdWxpL2tvbGF5L3VpL3NyYy9jb21wb25lbnRzL2dyb3VwLW5hdi5ndHMiXSwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IENvbXBvbmVudCBmcm9tICdAZ2xpbW1lci9jb21wb25lbnQnO1xuaW1wb3J0IHsgc2VydmljZSB9IGZyb20gJ0BlbWJlci9zZXJ2aWNlJztcblxuaW1wb3J0IHR5cGUgRG9jc1NlcnZpY2UgZnJvbSAnLi4vc2VydmljZXMva29sYXkvZG9jcy50cyc7XG5pbXBvcnQgdHlwZSBSb3V0ZXJTZXJ2aWNlIGZyb20gJ0BlbWJlci9yb3V0aW5nL3JvdXRlci1zZXJ2aWNlJztcblxuZXhwb3J0IGNsYXNzIEdyb3VwTmF2IGV4dGVuZHMgQ29tcG9uZW50PHtcbiAgLyoqXG4gICAqIFRoZSBgPG5hdj5gIGVsZW1lbnQuIEl0IGhhcyBhIGRlZmF1bHQgYGFyaWEtbGFiZWxgIG9mIFwiR3JvdXBzXCIuXG4gICAqIE5vcm1hbGx5IGFuIGBhcmlhLWxhYmVsYCBpcyBub3QgcmVxdWlyZWQsXG4gICAqIGJ1dCB3aGVuIHRoZXJlIGFyZSBtdWx0aXBsZSBgPG5hdj5gIGVsZW1lbnRzIG9uIGEgc2NyZWVuLCBpdCBpcyByZXF1aXJlZC5cbiAgICovXG4gIEVsZW1lbnQ6IEhUTUxFbGVtZW50O1xuICBBcmdzOiB7XG4gICAgLyoqXG4gICAgICogVGhlIGNsYXNzIHRvIGFwcGx5IHRvIHRoZSBgPGE-YCBlbGVtZW50IHdoZW4gaXRzIGxpbmsgaXMgYWN0aXZlLlxuICAgICAqXG4gICAgICogRGVmYXVsdHMgdG8gXCJhY3RpdmVcIlxuICAgICAqL1xuICAgIGFjdGl2ZUNsYXNzPzogc3RyaW5nO1xuICAgIC8qKlxuICAgICAqIFRoZSB0ZXh0IHRvIHVzZSBmb3IgdGhlIFwiL1wiIGxpbmsuXG4gICAgICpcbiAgICAgKiBEZWZhdWx0cyB0byBcIkhvbWVcIi5cbiAgICAgKiBUaGlzIHR5cGUgb2YgbGluayBpcyBjb21tb25seSBzZXQgdG8gdGhlIGxpYnJhcnkgbmFtZS5cbiAgICAgKi9cbiAgICBob21lTmFtZT86IHN0cmluZztcbiAgfTtcbiAgQmxvY2tzOiB7XG4gICAgLyoqXG4gICAgICogSWYgeW91J2QgbGlrZSB0byBjdXN0b21pemUgaG93IHRoZSBsaW5rcyBhcmUgZm9ybWF0dGVkLFxuICAgICAqIHlvdSBtYXkgcGFzcyBhIGJsb2NrIHdoaWNoIHdoaWNoIHdpbGwgaGF2ZSB0aGUgbmFtZSBvZiBlYWNoIGxpbmtcbiAgICAgKiB5aWVsZGVkIHRvIGl0LlxuICAgICAqXG4gICAgICogRXhhbXBsZTpcbiAgICAgKiBgYGBnanNcbiAgICAgKiBpbXBvcnQgeyBHcm91cE5hdiB9IGZyb20gJ2tvbGF5L2NvbXBvbmVudHMnO1xuICAgICAqXG4gICAgICogY29uc3QgZm9ybWF0ID0gdGV4dCA9PiB0ZXh0LnRvVXBwZXJDYXNlKCk7XG4gICAgICpcbiAgICAgKiA8dGVtcGxhdGU-XG4gICAgICogICA8R3JvdXBOYXYgYXMgfG5hbWV8PlxuICAgICAqICAgICB7e2Zvcm1hdCBuYW1lfX1cbiAgICAgKiAgIDwvR3JvdXBOYXY-XG4gICAgICogPC90ZW1wbGF0ZT5cbiAgICAgKiBgYGBcbiAgICAgKi9cbiAgICBkZWZhdWx0OiBbbmFtZTogc3RyaW5nXTtcbiAgfTtcbn0-IHtcbiAgQHNlcnZpY2UoJ2tvbGF5L2RvY3MnKSBkZWNsYXJlIGRvY3M6IERvY3NTZXJ2aWNlO1xuICBAc2VydmljZSBkZWNsYXJlIHJvdXRlcjogUm91dGVyU2VydmljZTtcblxuICBnZXQgaG9tZU5hbWUoKSB7XG4gICAgcmV0dXJuIHRoaXMuYXJncy5ob21lTmFtZSA_PyAnSG9tZSc7XG4gIH1cblxuICBnZXQgZ3JvdXBzKCkge1xuICAgIHJldHVybiB0aGlzLmRvY3MuYXZhaWxhYmxlR3JvdXBzLm1hcCgoZ3JvdXBOYW1lKSA9PiB7XG4gICAgICBpZiAoZ3JvdXBOYW1lID09PSAncm9vdCcpIHJldHVybiB7IHRleHQ6IHRoaXMuaG9tZU5hbWUsIHZhbHVlOiAnLycgfTtcblxuICAgICAgcmV0dXJuIHsgdGV4dDogZ3JvdXBOYW1lLCB2YWx1ZTogYC8ke2dyb3VwTmFtZX1gIH07XG4gICAgfSk7XG4gIH1cblxuICBpc0FjdGl2ZSA9IChzdWJQYXRoOiBzdHJpbmcpID0-IHtcbiAgICBpZiAoc3ViUGF0aCA9PT0gJy8nKSByZXR1cm4gZmFsc2U7XG5cbiAgICByZXR1cm4gdGhpcy5yb3V0ZXIuY3VycmVudFVSTD8uc3RhcnRzV2l0aChzdWJQYXRoKTtcbiAgfTtcblxuICBnZXQgYWN0aXZlQ2xhc3MoKSB7XG4gICAgcmV0dXJuIHRoaXMuYXJncy5hY3RpdmVDbGFzcyA_PyAnYWN0aXZlJztcbiAgfVxuXG4gIDx0ZW1wbGF0ZT5cbiAgICA8bmF2IGFyaWEtbGFiZWw9J0dyb3VwcycgLi4uYXR0cmlidXRlcz5cbiAgICAgIDx1bD5cbiAgICAgICAge3sjZWFjaCB0aGlzLmdyb3VwcyBhcyB8Z3JvdXB8fX1cbiAgICAgICAgICA8bGk-XG4gICAgICAgICAgICA8YVxuICAgICAgICAgICAgICBocmVmPXt7Z3JvdXAudmFsdWV9fVxuICAgICAgICAgICAgICBjbGFzcz17e2lmICh0aGlzLmlzQWN0aXZlIGdyb3VwLnZhbHVlKSB0aGlzLmFjdGl2ZUNsYXNzfX1cbiAgICAgICAgICAgID5cblxuICAgICAgICAgICAgICB7eyNpZiAoaGFzLWJsb2NrKX19XG4gICAgICAgICAgICAgICAge3t5aWVsZCBncm91cC50ZXh0fX1cbiAgICAgICAgICAgICAge3tlbHNlfX1cbiAgICAgICAgICAgICAgICB7e2dyb3VwLnRleHR9fVxuICAgICAgICAgICAgICB7ey9pZn19XG5cbiAgICAgICAgICAgIDwvYT5cbiAgICAgICAgICA8L2xpPlxuICAgICAgICB7ey9lYWNofX1cbiAgICAgIDwvdWw-XG4gICAgPC9uYXY-XG4gIDwvdGVtcGxhdGU-XG59XG4iXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IjtBQUFBLE9BQU8sZUFBZSxxQkFBcUI7QUFDM0MsU0FBUyxPQUFPLFFBQVEsaUJBQWlCO0FBRXpDLFlBQVksaUJBQWlCLDRCQUE0QjtBQUN6RCxZQUFZLG1CQUFtQixnQ0FBZ0M7QUFFL0QsT0FBTyxNQUFNLGlCQUFpQjtJQUM1Qjs7OztHQUlDLEdBQ0QsU0FBUztJQUNUO1FBQ0U7Ozs7S0FJQyxHQUNELGNBQWMsTUFBTTtRQUNwQjs7Ozs7S0FLQyxHQUNELFdBQVcsTUFBTTs7SUFFbkI7UUFDRTs7Ozs7Ozs7Ozs7Ozs7Ozs7S0FpQkMsR0FDRCxVQUFVLE1BQU0sTUFBTTs7O0lBR3hCLENBQUMsUUFBUTtJQUFzQixNQUFNLFlBQVk7SUFDakQsQ0FBQztJQUFnQixRQUFRLGNBQWM7SUFFdkMsSUFBSSxXQUFXO1FBQ2IsT0FBTyxJQUFJLENBQUMsSUFBSSxDQUFDLFFBQVEsSUFBSTtJQUMvQjtJQUVBLElBQUksU0FBUztRQUNYLE9BQU8sSUFBSSxDQUFDLElBQUksQ0FBQyxlQUFlLENBQUMsR0FBRyxDQUFDLENBQUM7WUFDcEMsSUFBSSxlQUFjLFFBQVEsT0FBTztnQkFBRSxNQUFNLElBQUksQ0FBQyxRQUFRO2dCQUFFLE9BQU87WUFBSTtZQUVuRSxPQUFPO2dCQUFFLE1BQU07Z0JBQVcsT0FBTyxDQUFDLENBQUMsRUFBRSxXQUFVLENBQUM7WUFBQztRQUNuRDtJQUNGO0lBRUEsV0FBVyxDQUFDLFVBQVMsTUFBTTtRQUN6QixJQUFJLGFBQVksS0FBSyxPQUFPO1FBRTVCLE9BQU8sSUFBSSxDQUFDLE1BQU0sQ0FBQyxVQUFVLEVBQUUsV0FBVztJQUM1QyxFQUFFO0lBRUYsSUFBSSxjQUFjO1FBQ2hCLE9BQU8sSUFBSSxDQUFDLElBQUksQ0FBQyxXQUFXLElBQUk7SUFDbEM7SUFFQSxNQUFBO1FBQUEsU0FBVSxDQUFBOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7RUFxQlYsQ0FBQSxFQUFBO1lBQUEsV0FBQSxJQUFXO1lBQVg7Z0JBQUEsT0FBQSxLQUFBLFNBQUEsQ0FBQSxFQUFXO1lBQUQ7UUFBQTtJQUFBLENBckJBO0FBc0JaIn0